import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { FileText, Shield, Users, Zap, ArrowRight, Check, Brain, MessageSquare } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Landing() {
  const navigate = useNavigate();

  const features = [
    {
      icon: FileText,
      title: "Génération Intelligente",
      description: "Créez des contrats professionnels à partir de modèles Word avec détection automatique des champs."
    },
    {
      icon: MessageSquare,
      title: "Assistant IA (GPT-5.2)",
      description: "Générez des contrats par conversation, obtenez des suggestions de clauses et simplifiez le langage juridique."
    },
    {
      icon: Brain,
      title: "Analyse IA (Claude)",
      description: "Analyse de risques, détection de clauses problématiques et recommandations stratégiques avec Claude Opus & Sonnet."
    },
    {
      icon: Users,
      title: "Multi-Parties",
      description: "Gérez facilement les contrats impliquant plusieurs parties avec des blocs répétables dynamiques."
    },
    {
      icon: Shield,
      title: "Versioning Avancé",
      description: "Suivez chaque modification avec un historique complet et la possibilité de restaurer des versions."
    },
    {
      icon: Zap,
      title: "Gestion de Cabinet",
      description: "Clients, dossiers, facturation et stockage sécurisé dans le cloud pour votre cabinet."
    }
  ];

  const benefits = [
    "Gestion centralisée des modèles de contrats",
    "Formulaires dynamiques générés automatiquement",
    "Export Word et PDF professionnels",
    "Collaboration d'équipe avec gestion des rôles",
    "Historique complet des modifications",
    "Interface intuitive inspirée de Notion"
  ];

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const handleGoogleLogin = () => {
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white" data-testid="landing-page">
      {/* Header */}
      <header className="glass-header sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center shadow-sm">
              <FileText className="w-5 h-5 text-white" />
            </div>
            <span className="font-heading font-semibold text-xl text-blue-900">Lexora LegalTech</span>
          </div>
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/login')} className="text-slate-600 hover:text-blue-600" data-testid="login-nav-btn">
              Connexion
            </Button>
            <Button onClick={() => navigate('/register')} className="bg-blue-600 hover:bg-blue-700" data-testid="register-nav-btn">
              Commencer gratuitement
            </Button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="py-20 lg:py-32">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <div className="space-y-4">
                <p className="tracking-[0.2em] text-xs font-medium text-muted-foreground uppercase">
                  Plateforme CLM
                </p>
                <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight">
                  Gestion intelligente de vos contrats juridiques
                </h1>
                <p className="text-lg text-muted-foreground leading-relaxed max-w-xl">
                  Simplifiez la création, l'analyse et le suivi de vos contrats avec GPT-5.2 et Claude. 
                  Conçu pour les cabinets d'avocats et les entreprises modernes.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  size="lg" 
                  onClick={() => navigate('/register')} 
                  className="gap-2 bg-blue-600 hover:bg-blue-700"
                  data-testid="hero-cta-btn"
                >
                  Démarrer maintenant
                  <ArrowRight className="w-4 h-4" />
                </Button>
                <Button 
                  size="lg" 
                  variant="outline" 
                  onClick={handleGoogleLogin}
                  className="border-blue-200 hover:bg-blue-50"
                  data-testid="google-login-btn"
                >
                  <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                    <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  Continuer avec Google
                </Button>
              </div>
            </div>
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1763833294545-e38e4fab1961?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzV8MHwxfHNlYXJjaHwxfHxjbGVhbiUyMGxlZ2FsJTIwZG9jdW1lbnRzJTIwb24lMjBkZXNrJTIwbWluaW1hbGlzdHxlbnwwfHx8fDE3NzAxMzc0MzB8MA&ixlib=rb-4.1.0&q=85"
                alt="Lexora LegalTech Dashboard"
                className="rounded-lg shadow-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <p className="tracking-[0.2em] text-xs font-medium text-blue-600 uppercase mb-4">
              Fonctionnalités
            </p>
            <h2 className="font-heading text-3xl font-semibold tracking-tight text-slate-800">
              Tout ce dont vous avez besoin
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <div 
                key={index}
                className="bg-white p-6 rounded-lg border border-blue-100 card-hover"
                data-testid={`feature-card-${index}`}
              >
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                  <feature.icon className="w-6 h-6 text-blue-600" />
                </div>
                <h3 className="font-semibold mb-2 text-slate-800">{feature.title}</h3>
                <p className="text-sm text-slate-500">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <img 
                src="https://images.unsplash.com/photo-1545157936-99797cf20a76?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzV8MHwxfHNlYXJjaHwyfHxjbGVhbiUyMGxlZ2FsJTIwZG9jdW1lbnRzJTIwb24lMjBkZXNrJTIwbWluaW1hbGlzdHxlbnwwfHx8fDE3NzAxMzc0MzB8MA&ixlib=rb-4.1.0&q=85"
                alt="Contract Management"
                className="rounded-lg shadow-lg"
              />
            </div>
            <div className="space-y-8">
              <div>
                <p className="tracking-[0.2em] text-xs font-medium text-muted-foreground uppercase mb-4">
                  Pourquoi Lexora LegalTech
                </p>
                <h2 className="font-heading text-3xl font-semibold tracking-tight mb-4">
                  Conçu pour les professionnels du droit
                </h2>
                <p className="text-muted-foreground">
                  Notre plateforme combine simplicité d'utilisation et puissance fonctionnelle 
                  pour répondre aux besoins des cabinets d'avocats et des services juridiques.
                </p>
              </div>
              <ul className="space-y-3">
                {benefits.map((benefit, index) => (
                  <li key={index} className="flex items-center gap-3">
                    <div className="w-5 h-5 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <Check className="w-3 h-3 text-blue-600" />
                    </div>
                    <span className="text-sm">{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-blue-700 text-white">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="font-heading text-3xl font-semibold tracking-tight mb-4">
            Prêt à transformer votre gestion contractuelle ?
          </h2>
          <p className="text-blue-100 mb-8">
            Rejoignez les juristes et entreprises qui font confiance à Lexora LegalTech 
            pour simplifier leur workflow documentaire.
          </p>
          <Button 
            size="lg" 
            variant="secondary"
            onClick={() => navigate('/register')}
            className="bg-white text-blue-600 hover:bg-blue-50"
            data-testid="cta-register-btn"
          >
            Créer un compte gratuit
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 border-t border-blue-100 bg-white">
        <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 bg-gradient-to-br from-blue-500 to-blue-600 rounded flex items-center justify-center">
              <FileText className="w-4 h-4 text-white" />
            </div>
            <span className="font-semibold">Lexora LegalTech</span>
          </div>
          <p className="text-sm text-muted-foreground">
            © 2024 Lexora LegalTech. Plateforme CLM pour juristes modernes.
          </p>
        </div>
      </footer>
    </div>
  );
}
